import React, { useCallback, useMemo, useState } from 'react';
import { Button } from '@primer/react';

import capitalize from 'lodash/capitalize';

import compareDesc from 'date-fns/compareDesc';
import parseISO from 'date-fns/parseISO';

import { formatOrchestrationRunDate, getNextRunFromCronString } from '../../../utils/date_utils';
import { getRunTypeIcon } from '../../../utils/task_utils';
import { getThemeColor } from '../../../utils/theme-utils';

import AlternatingRowDataTable from '../../common/DataTable/AlternatingRowDataTable';
import Icon from '../../common/icons/Icon';
import Label from '../../common/Labels/Label';
import StopIcon from '../../common/icons/StopIcon';

import KillTaskConfirmationModal from '../KillTaskConfirmationModal/KillTaskConfirmationModal';

import killTask from '../../../utils/api_functions/killTask';
import useWorkspacePermissions from '../../../utils/hooks/use_workspace_permissions';
import WithTooltip from '../../common/TooltipWrapper/TooltipWrapper';

const transformTaskListData = (taskListRows = []) => taskListRows?.map(taskListItem => ({
  id: taskListItem?.id,
  name: taskListItem?.name,
  runType: taskListItem?.run_type,
  lastRuns: taskListItem?.last_runs,
  schedule: taskListItem?.schedule,
  status: capitalize(taskListItem?.status),
  killed: taskListItem?.killed,
  created_at: taskListItem?.created_at,
}))

const sortTaskData = (taskRows = []) => taskRows.sort((itemA, itemB) => {
  const firstDate = parseISO(itemA.created_at);
  const secondDate = parseISO(itemB.created_at);

  return compareDesc(firstDate, secondDate);
})

// Oneshot, CronJob, NetworkService
const RunTypeIndicator = ({ runType }) => {
  const chosenIcon = getRunTypeIcon(runType);
  return (
    <Icon
      icon={chosenIcon}
      fill={getThemeColor('brand')}
    />
  )
}

const LastRunAtCellRenderer = ({ row, value }) => {
  const lastRunDate = value?.length ? value[value.length - 1]?.created_at : null;
  const nextRunDate = row?.original?.schedule && getNextRunFromCronString(row?.original?.schedule, lastRunDate ? lastRunDate : new Date());
  const schedule = row?.original?.schedule;

  const calculateDateToBeDisplayed = () => { 
    if (!lastRunDate) return '--'
    
    return `${formatOrchestrationRunDate(lastRunDate)}`
  }
  
  return (
    <div className="flex flex-col p-2">
      <div className="flex flex-col flex-wrap items-start mb-2">
        <div className="flex">
          <Label>{calculateDateToBeDisplayed()}</Label>
        </div>
        {
          schedule && nextRunDate && <Label size="sm">Next Scheduled Run: {formatOrchestrationRunDate(nextRunDate)}</Label>
        }
      </div>
    </div>
  )
}

const ActionCellRenderer = ({
  row, meta = {}
}) => {
  const { canModify } = useWorkspacePermissions();
  const buttonStyle = {
    padding: 0,
    border: 'none',
    boxShadow: 'none',
  }

  return (
    <div className="flex flex-col items-center">
      <WithTooltip label="Insufficient workspace permissions" showTooltip={!canModify}>
        <Button sx={buttonStyle} onClick={() => meta?.setTaskToKill(row?.original?.id)} disabled={!canModify}>
          <Icon
            icon={StopIcon}
            width={16}
            height={16}
            fill={getThemeColor('brand')}
          />
        </Button>
      </WithTooltip>
    </div>
  )
}

const TaskCellRenderer = ({ row, value }) => {
  const name = row?.original?.name;
  const runType = row?.original?.runType;

  return (
    <div className="flex flex-col p-2">
      <div className="flex flex-col flex-wrap items-start mb-2">
        <div className="flex items-center justify-center">
          <RunTypeIndicator runType={runType} />
          <Label
            className="ml-2"
            size="md"
            title={value}
            bold
          >
            {name}
          </Label>
        </div>
        <Label size="md" title={value}>
          {value}
        </Label>
      </div>
    </div>
  )
}

export const TaskListDataTable = ({ taskListData = [], refetchTasks = () => {}, hideScrollFading = false }) => {
  const [showKillTaskModal, setTaskToKill] = useState(null);

  const handleKillTask = useCallback(taskId => {
    killTask(taskId)
    setTimeout(() => refetchTasks(), 1000);
  }, [refetchTasks])

  const columns = useMemo(() => [
    {
      Header: 'Task',
      accessor: 'id',
      Cell: TaskCellRenderer,
      width: 60
    },
    {
      Header: 'Last Run at',
      accessor: 'lastRuns', // using last_runs[0]
      Cell: LastRunAtCellRenderer,
      width: 80,
      tooltipText: 'Next scheduled time calculated according to CRON settings. Actual run time might differ due to multiple factors.',
    },
    {
      Header: 'Action',
      Cell: ActionCellRenderer,
      width: 40
    }
  ], []);
  
  const data = useMemo(() => sortTaskData(transformTaskListData(taskListData)), [taskListData]);

  const defaultColumn = React.useMemo(
    () => ({
      width: 160,
    }),
    []
  )

  const onModalToggle = (showModal) => {
    if (!showModal) {
      setTaskToKill(null);
    }
  }

  return (
    <>
      <AlternatingRowDataTable
        id="tasklist-data-table"
        columns={columns}
        data={data}
        defaultColumn={defaultColumn}
        pagination={null}
        meta={{
          setTaskToKill
        }}
        hideScrollFading={hideScrollFading}
      />
      {
        showKillTaskModal && (
          <KillTaskConfirmationModal
            taskId={showKillTaskModal}
            showDialog={showKillTaskModal}
            onKillTask={handleKillTask}
            onDialogToggle={onModalToggle}
          />
        )
      }
    </>
  )
};

export default TaskListDataTable;
